/**
 * FREE TASTE WAR ROOM
 * A single free Living War Room debate for unregistered visitors.
 * Shows the full experience with a conversion wall after.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Loader2, Sparkles, ArrowRight, RefreshCw, Lock,
  Target, Zap, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GOD_CONFIG = {
  calliope: { name: 'Calliope', emoji: '\u{1F3AD}', title: 'Creative Wildcard', gradient: 'from-pink-500 to-purple-600', text: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/30', glow: 'rgba(236,72,153,0.3)' },
  eris: { name: 'Eris', emoji: '\u2694\uFE0F', title: 'The Contrarian', gradient: 'from-red-600 to-pink-600', text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', glow: 'rgba(239,68,68,0.3)' },
  plutus: { name: 'Plutus', emoji: '\u{1F4B0}', title: 'The CFO', gradient: 'from-green-500 to-emerald-600', text: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30', glow: 'rgba(34,197,94,0.3)' },
  hestia: { name: 'Hestia', emoji: '\u{1F3E0}', title: 'Customer Advocate', gradient: 'from-orange-400 to-amber-500', text: 'text-orange-300', bg: 'bg-orange-400/10', border: 'border-orange-400/30', glow: 'rgba(251,146,60,0.3)' },
  themis: { name: 'Themis', emoji: '\u2696\uFE0F', title: 'Data Purist', gradient: 'from-blue-600 to-cyan-600', text: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30', glow: 'rgba(59,130,246,0.3)' },
  nike: { name: 'Nike', emoji: '\u{1F3C6}', title: 'Growth Hacker', gradient: 'from-yellow-400 to-orange-500', text: 'text-yellow-300', bg: 'bg-yellow-400/10', border: 'border-yellow-400/30', glow: 'rgba(250,204,21,0.3)' },
  metis: { name: 'Metis', emoji: '\u{1F9E0}', title: 'Grand Strategist', gradient: 'from-indigo-600 to-purple-700', text: 'text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', glow: 'rgba(99,102,241,0.3)' },
  athena: { name: 'Athena', emoji: '\u{1F989}', title: 'Strategic Intel', gradient: 'from-amber-500 to-orange-600', text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', glow: 'rgba(245,158,11,0.3)' },
  hermes: { name: 'Hermes', emoji: '\u{1F45F}', title: 'Content God', gradient: 'from-orange-500 to-red-600', text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', glow: 'rgba(249,115,22,0.3)' },
  apollo: { name: 'Apollo', emoji: '\u2600\uFE0F', title: 'Distribution', gradient: 'from-yellow-500 to-orange-500', text: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', glow: 'rgba(234,179,8,0.3)' },
  dionysus: { name: 'Dionysus', emoji: '\u{1F377}', title: 'Viral Growth', gradient: 'from-purple-600 to-red-600', text: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30', glow: 'rgba(147,51,234,0.3)' },
};

const FreeTasteWarRoom = () => {
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [activeGods, setActiveGods] = useState([]);
  const [topic, setTopic] = useState('');
  const [verdict, setVerdict] = useState(null);
  const [visibleCount, setVisibleCount] = useState(0);
  const [typingGod, setTypingGod] = useState(null);
  const [error, setError] = useState(null);
  const chatEndRef = useRef(null);
  const animRef = useRef(null);

  const scrollToBottom = () => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  const animateMessages = (msgs) => {
    if (!msgs.length) return;
    let step = 0;
    const showNext = () => {
      if (step < msgs.length) {
        setTypingGod(msgs[step]?.agent || 'calliope');
        animRef.current = setTimeout(() => {
          setTypingGod(null);
          setVisibleCount(prev => prev + 1);
          step++;
          setTimeout(scrollToBottom, 50);
          if (step < msgs.length) {
            animRef.current = setTimeout(showNext, 800);
          }
        }, 1500);
      }
    };
    animRef.current = setTimeout(showNext, 600);
  };

  const startFreeDebate = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/war-council/living-room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (!res.ok) throw new Error('Failed to start');
      const data = await res.json();
      setTopic(data.topic);
      setMessages(data.messages || []);
      setActiveGods(data.gods || []);
      setVerdict(data.verdict || null);
      setStarted(true);
      setLoading(false);
      animateMessages(data.messages || []);
    } catch (err) {
      setError('The gods are resting. Try again in a moment.');
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => { if (animRef.current) clearTimeout(animRef.current); };
  }, []);

  // Pre-start: invitation to try
  if (!started && !loading) {
    return (
      <div className="w-full" data-testid="free-taste-invite">
        <div className="rounded-2xl border border-purple-500/20 bg-gradient-to-b from-purple-900/20 to-gray-900/60 overflow-hidden">
          <div className="px-6 py-8 text-center">
            <div className="flex items-center justify-center gap-1 mb-4">
              {['eris','calliope','plutus','nike'].map((id) => {
                const g = GOD_CONFIG[id];
                return (
                  <div key={id} className={`w-10 h-10 rounded-full bg-gradient-to-br ${g.gradient} flex items-center justify-center border-2 border-gray-900 -mx-1 shadow-lg`}>
                    <span className="text-base">{g.emoji}</span>
                  </div>
                );
              })}
            </div>
            <h3 className="text-xl font-bold text-white mb-2">See the Gods in Action</h3>
            <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
              Watch 4 AI gods debate a real marketing strategy question — live, right now. No signup needed.
            </p>
            <button
              onClick={startFreeDebate}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-medium text-sm hover:from-purple-500 hover:to-cyan-500 transition-all"
              data-testid="free-taste-start"
            >
              <Sparkles className="w-4 h-4" /> Start a Free Debate
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="w-full">
        <div className="rounded-2xl border border-purple-500/20 bg-gray-900/50 px-6 py-12 text-center">
          <Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-3" />
          <p className="text-gray-300 text-sm">The gods are convening...</p>
          <p className="text-gray-600 text-xs mt-1">This takes about 20 seconds</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full">
        <div className="rounded-2xl border border-red-500/20 bg-gray-900/50 px-6 py-8 text-center">
          <AlertTriangle className="w-6 h-6 text-red-400 mx-auto mb-2" />
          <p className="text-gray-400 text-sm mb-3">{error}</p>
          <button onClick={startFreeDebate} className="text-purple-400 text-xs hover:text-white transition-colors">Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="free-taste-debate">
      <div className="rounded-2xl border border-purple-500/20 bg-gray-900/50 overflow-hidden">
        {/* Topic */}
        <div className="px-5 py-3 bg-gradient-to-r from-purple-900/30 via-gray-900/50 to-indigo-900/30 border-b border-purple-500/10">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[10px] text-gray-500 uppercase tracking-widest">Live Debate</span>
          </div>
          <p className="text-purple-200/80 italic text-sm">"{topic}"</p>
          <div className="flex items-center gap-1.5 flex-wrap mt-2">
            {activeGods.map((id) => {
              const god = GOD_CONFIG[id];
              if (!god) return null;
              return (
                <span key={id} className={`text-[11px] px-1.5 py-0.5 rounded-full ${god.bg} ${god.border} border ${god.text}`}>
                  {god.emoji} {god.name}
                </span>
              );
            })}
          </div>
        </div>

        {/* Messages */}
        <div className="px-4 py-4 space-y-3 max-h-[500px] overflow-y-auto">
          {messages.slice(0, visibleCount).map((msg, i) => {
            const god = GOD_CONFIG[msg.agent] || GOD_CONFIG.calliope;
            return (
              <div key={i} className="flex items-start gap-2.5 animate-fade-in">
                <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${god.gradient} flex items-center justify-center flex-shrink-0 shadow`} style={{ boxShadow: `0 0 8px ${god.glow}` }}>
                  <span className="text-xs">{god.emoji}</span>
                </div>
                <div className={`flex-1 px-3 py-2.5 rounded-2xl rounded-tl-sm bg-gray-800/40 border ${god.border}`}>
                  <span className={`text-xs font-semibold ${god.text}`}>{god.name}</span>
                  <p className="text-gray-200 text-sm leading-relaxed mt-1">{msg.content}</p>
                </div>
              </div>
            );
          })}

          {typingGod && GOD_CONFIG[typingGod] && (
            <div className="flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${GOD_CONFIG[typingGod].gradient} flex items-center justify-center`}>
                <span className="text-xs">{GOD_CONFIG[typingGod].emoji}</span>
              </div>
              <div className="flex gap-1 px-3 py-2">
                {[0,1,2].map(i => <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce" style={{ animationDelay: `${i*150}ms` }} />)}
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Verdict (if loaded) */}
        {verdict?.verdict && visibleCount >= messages.length && !typingGod && (
          <div className="px-4 pb-3">
            <div className="rounded-xl border border-amber-500/30 bg-gradient-to-b from-amber-900/20 to-gray-900/50 p-3.5">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs font-semibold text-amber-300 uppercase tracking-wider">The Verdict</span>
              </div>
              <p className="text-sm text-gray-200">{verdict.verdict}</p>
              {verdict.primary_action && (
                <div className="flex items-start gap-2 mt-2 p-2 rounded-lg bg-purple-500/10 border border-purple-500/20">
                  <Zap className="w-3 h-3 text-purple-400 mt-0.5" />
                  <p className="text-xs text-gray-300">{verdict.primary_action}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Conversion Wall */}
        {visibleCount >= messages.length && !typingGod && (
          <div className="px-4 pb-5">
            <div className="rounded-xl border border-purple-500/30 bg-gradient-to-r from-purple-900/30 via-gray-900/50 to-cyan-900/30 p-5 text-center">
              <Lock className="w-5 h-5 text-purple-400 mx-auto mb-2" />
              <h4 className="text-base font-bold text-white mb-1">Want this personalized to YOUR business?</h4>
              <p className="text-gray-400 text-xs mb-4 max-w-sm mx-auto">
                Sign up and the gods will pull your real metrics, remember your past debates, and give you specific strategy.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-medium text-sm hover:from-purple-500 hover:to-cyan-500 transition-all"
                data-testid="free-taste-signup"
              >
                Get Started Free <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FreeTasteWarRoom;
